// src/components/dashboards/add_patient_v2/components/TabularWizardModal.tsx
// -----------------------------------------------------------------------------
// TabularWizardModal — wraps the existing TabularWizard in a Dialog so the V2
// cards UI can open the CSV/Excel import flow without leaving the form.
//
// Why a wrapper instead of mounting TabularWizard directly: the wizard manages
// its own internal state (file parse, schema inference, polling) and is
// already a self-contained 6-step component. Embedding it inline inside a
// PatientCard would be cramped and would force re-renders of the cards stack
// during long-running steps. A modal cleanly isolates the surfaces.
//
// On wizard completion (`onComplete` fires from the inner wizard once its
// own Celery job finishes processing), we close the modal and bubble the
// completion event to the V2 form so it can refresh / show a snackbar /
// trigger onBulkComplete.
//
// "Review in cards" — deferred. See cursor_logs/030 § 2026-05-22 cards-UI
// design ("Per-patient document content — header + relevant rows"). The
// wizard's Confirm & Start path remains the single entry point in Phase 5;
// landing review-in-cards requires either (a) a new backend endpoint that
// accepts per-row card overrides, or (b) treating spreadsheet-derived cards
// as read-only summaries — both deserve their own design pass.
// -----------------------------------------------------------------------------

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";

import { useTranslation } from "../../../../i18n";
import TabularWizard, { StagedTabularPatient } from "../../TabularWizard";
import {
  athenaDarkPaperSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaColors,
} from "../../../layout/AthenaDialogStyles";

export interface TabularWizardModalProps {
  open: boolean;
  /** Spreadsheet the user dropped on the form. Handed straight to the
   *  wizard so it parses immediately instead of re-prompting for a file
   *  (Joan, 2026-05-27). */
  initialFile?: File | null;
  /** Closes the modal without submitting. Files dropped earlier in the V2
   *  form are unaffected; only the wizard's transient parse state is lost. */
  onClose: () => void;
  /** Fires after the wizard's internal Celery job finishes (direct-submit
   *  path). The V2 form uses it to bubble completion to the parent. */
  onComplete: () => void;
  /** Fires when the user clicks "Review & edit in cards" inside the wizard.
   *  Hands back the staged per-patient prompts so the V2 form can materialize
   *  editable cards. The modal closes after invocation.
   *  Design reference: cursor_logs/030 § 2026-05-22 editable wizard cards. */
  onReviewInCards: (payload: {
    uploadId: string;
    patients: StagedTabularPatient[];
  }) => void;
  environmentId: string;
  /** All ticked environments (cursor_logs/043). */
  environmentIds?: string[];
}

const TabularWizardModal: React.FC<TabularWizardModalProps> = ({
  open,
  initialFile,
  onClose,
  onComplete,
  onReviewInCards,
  environmentId,
  environmentIds,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  const handleReviewInCards = (payload: {
    uploadId: string;
    patients: StagedTabularPatient[];
  }) => {
    onReviewInCards(payload);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="tabular-wizard-modal-title"
      PaperProps={{ sx: athenaDarkPaperSx }}
    >
      <DialogTitle
        id="tabular-wizard-modal-title"
        sx={{ ...athenaDarkTitleSx, pr: 1 }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t(
            "patients_v2.tabular_modal_title",
            "Import patients from spreadsheet",
          )}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t("action.close", "Close")}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          ...athenaDarkContentSx,
          // cursor_logs/036 §6a: a couple of spots inside TabularWizard set
          // their text color via inline `sx`/`style` to `theme.palette.text.secondary`
          // (dark navy) rather than via MUI color classes — designed for the V1
          // standalone mount on a light page. On this dark gradient surface they
          // read as near-black. The shared className overrides can't reach them,
          // so promote them here (scoped to this modal only; the V1 mount is
          // untouched). The ASCII help block is a `<Box component="pre">`; its
          // sx-generated color class is beaten by this more specific descendant
          // rule with !important.
          "& pre": {
            color: `${alpha(theme.palette.common.white, 0.7)} !important`,
            borderColor: alpha(athenaColors.periwinkle, 0.25),
            backgroundColor: alpha(theme.palette.common.white, 0.04),
          },
        }}
      >
        <Box sx={{ minHeight: 400 }}>
          {/* Render the wizard fresh each time the modal opens so its
              internal state (parseResult, schemaInferred, jobId, etc.)
              resets between imports. */}
          {open && (
            <TabularWizard
              key={`wizard-${environmentId}-${open ? "open" : "closed"}`}
              environmentId={environmentId}
              environmentIds={environmentIds}
              dark
              initialFile={initialFile}
              onComplete={handleComplete}
              onReviewInCards={handleReviewInCards}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TabularWizardModal;
